import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { getCookie } from "../../Utils/cookies";
import { useAuth } from "../../Context/MyContext";
import * as XLSX from 'xlsx';
import {
  FaCheck,
  FaTimes,
  FaArrowUp,
  FaSpinner,
  FaDownload,
  FaBan,
  FaFileExcel,
  FaUpload,
  FaInfoCircle,
} from "react-icons/fa";

const VerifyImage = () => {
  const { logoutAPI } = useAuth();
  const empData = getCookie("empData") ? JSON.parse(getCookie("empData")) : {};
  const navigate = useNavigate();
  const [installationData, setInstallationData] = useState([]);
  const [filteredInstallations, setFilteredInstallations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedState, setSelectedState] = useState("Maharashtra");
  const [selectedStage, setSelectedStage] = useState("all");
  const [selectedImage, setSelectedImage] = useState({
    url: null,
    fieldName: null,
    saralId: null,
    date: null,
  });
  const [approvingId, setApprovingId] = useState(null);
  const [decliningId, setDecliningId] = useState(null);
  const [fetchProgress, setFetchProgress] = useState({ current: 0, total: 0 });

  // Excel upload states
  const [excelUploadModal, setExcelUploadModal] = useState(false);
  const [selectedExcelFile, setSelectedExcelFile] = useState(null);
  const [uploadingExcel, setUploadingExcel] = useState(false);
  const [bulkApproveResult, setBulkApproveResult] = useState(null);
  const [excelDataPreview, setExcelDataPreview] = useState([]);
  const [excelValidationErrors, setExcelValidationErrors] = useState([]);

  // Remark selection state
  const [remarksList, setRemarksList] = useState([]);
  const [selectedRemarkId, setSelectedRemarkId] = useState(null);
  const [remarkModalOpen, setRemarkModalOpen] = useState(false);
  const [declineInstallationId, setDeclineInstallationId] = useState(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const installationsGridRef = useRef(null);
  const [showScrollTop, setShowScrollTop] = useState(false);

  const getFullImageUrl = (url) => {
    if (!url) return "";
    if (url.startsWith("http")) return url;
    return `${process.env.REACT_APP_BASE_URL_API_URL}${url.startsWith("/") ? url : "/" + url}`;
  };

  const states = [
    "Maharashtra",
    "Haryana",
    "Punjab",
    "Rajasthan",
    "Chhattisgarh",
  ];

  const stageOptions = [
    { value: "all", label: "All Stages" },
    { value: "Approved By VT-2", label: "Approved By VT-2" },
    { value: "Rejected By VT-2", label: "Rejected By VT-2" },
    { value: "pending", label: "Pending Approval" },
  ];

  const getDisplayStatus = (installation) => {
    return installation.stageId?.stage || installation?.status || null;
  };

  const isFinalStatus = (status) => {
    return status === "Approved By VT-2" || status === "Rejected By VT-2";
  };

  const isPendingStatus = (status) => {
    return !status || (!isFinalStatus(status) && status !== "approved" && status !== "declined");
  };

  // Fetch all pages of data
  const fetchAllInstallationData = async (state, department) => {
    let allData = [];
    let currentPage = 1;
    let totalPages = 1;
    const limit = 20; // Fetch more records per request to reduce API calls

    try {
      // First request to get total pages
      const firstResponse = await axios.get(
        `${process.env.REACT_APP_BASE_URL_API_URL}/service-team/get-new-installation-data?state=${state}&department=${department}&page=${currentPage}&limit=${limit}`
      );

      if (!firstResponse.data.success) {
        throw new Error(firstResponse.data.message || "Failed to fetch installation data");
      }

      // Add first page data
      allData = [...firstResponse.data.data];
      totalPages = firstResponse.data.totalPages || 1;
      setFetchProgress({ current: 1, total: totalPages });

      // Fetch remaining pages in parallel if there are more pages
      if (totalPages > 1) {
        const pagePromises = [];
        for (let page = 2; page <= totalPages; page++) {
          pagePromises.push(
            axios.get(
              `${process.env.REACT_APP_BASE_URL_API_URL}/service-team/get-new-installation-data?state=${state}&department=${department}&page=${page}&limit=${limit}`
            )
          );
        }

        const pageResponses = await Promise.all(pagePromises);
        pageResponses.forEach((response, index) => {
          if (response.data.success && response.data.data) {
            allData = [...allData, ...response.data.data];
            setFetchProgress({ current: index + 2, total: totalPages });
          }
        });
      }

      return allData;
    } catch (error) {
      console.error("Error fetching all pages:", error);
      throw error;
    }
  };

  useEffect(() => {
    const fetchRemarks = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_BASE_URL_API_URL}/common/show-remarks`,
        );
        if (response.data.success) {
          setRemarksList(response.data.data);
        }
      } catch (err) {
        console.error("Error fetching remarks:", err);
      }
    };
    fetchRemarks();
  }, []);

  useEffect(() => {
    const fetchInstallationData = async () => {
      try {
        setLoading(true);
        setError(null);
        const department = empData?.department || "";
        
        // Fetch all data across all pages
        const allData = await fetchAllInstallationData(selectedState, department);
        
        setInstallationData(allData);
        setFilteredInstallations(allData);
        setLoading(false);
        setFetchProgress({ current: 0, total: 0 });
      } catch (err) {
        console.error("Error fetching installation data:", err);
        setError(err.message);
        setLoading(false);
        setFetchProgress({ current: 0, total: 0 });
      }
    };

    fetchInstallationData();
  }, [selectedState, empData?.department]);

  useEffect(() => {
    let filtered = [...installationData];
    if (searchTerm) {
      filtered = filtered.filter((installation) =>
        installation.farmerSaralId?.toLowerCase().includes(searchTerm.toLowerCase()),
      );
    }
    if (selectedStage !== "all") {
      filtered = filtered.filter((installation) => {
        const displayStatus = getDisplayStatus(installation);
        if (selectedStage === "pending") {
          return isPendingStatus(displayStatus);
        } else {
          return displayStatus === selectedStage;
        }
      });
    }
    setFilteredInstallations(filtered);
    setCurrentPage(1);
  }, [searchTerm, installationData, selectedStage]);

  useEffect(() => {
    const handleScroll = () => {
      if (installationsGridRef.current) {
        const position = installationsGridRef.current.scrollTop;
        setShowScrollTop(position > 300);
      }
    };
    const gridElement = installationsGridRef.current;
    if (gridElement) {
      gridElement.addEventListener("scroll", handleScroll);
      return () => gridElement.removeEventListener("scroll", handleScroll);
    }
  }, []);

  const totalPages = Math.ceil(filteredInstallations.length / itemsPerPage);
  const paginatedData = filteredInstallations.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const openImage = (imageUrl, fieldName, saralId, date) => {
    setSelectedImage({
      url: getFullImageUrl(imageUrl),
      fieldName: fieldName,
      saralId: saralId,
      date: date,
    });
    document.body.style.overflow = "hidden";
  };

  const closeImage = () => {
    setSelectedImage({
      url: null,
      fieldName: null,
      saralId: null,
      date: null,
    });
    document.body.style.overflow = "auto";
  };

  const downloadImage = async (imageUrl, fieldName, saralId, date) => {
    try {
      const fullUrl = getFullImageUrl(imageUrl);
      const response = await fetch(fullUrl);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      const formattedDate = date
        ? new Date(date).toISOString().split("T")[0]
        : new Date().toISOString().split("T")[0];
      const isVideo = /\.(mp4|webm|ogg|mov)$/i.test(fullUrl);
      const ext = isVideo ? ".mp4" : ".jpg";
      const filename = `${saralId || "installation"}_${fieldName || "file"}_${formattedDate}${ext}`;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Error downloading file:", error);
      alert("Failed to download file. Please try again.");
    }
  };

  const scrollToTop = () => {
    if (installationsGridRef.current) {
      installationsGridRef.current.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
  };

  const handleStateChange = (e) => {
    setSelectedState(e.target.value);
    setCurrentPage(1);
  };

  const handleStageChange = (e) => {
    setSelectedStage(e.target.value);
    setCurrentPage(1);
  };

  const handleApprove = async (installationId) => {
    if (window.confirm("Are you sure you want to approve this installation?")) {
      setApprovingId(installationId);
      try {
        const response = await axios.post(
          `${process.env.REACT_APP_BASE_URL_API_URL}/service-team/approve-installation-data`,
          {
            installationId,
            updatedByName: empData?.name,
            updatedByEmpId: empData?._id,
            department: empData?.department,
          },
        );
        if (response.data.success) {
          setInstallationData((prev) =>
            prev.map((item) =>
              item._id === installationId
                ? { ...item, stageId: { ...item.stageId, stage: "Approved By VT-2" }, status: "approved" }
                : item,
            ),
          );
          alert("Installation approved successfully");
        } else {
          throw new Error(response.data.message || "Failed to approve");
        }
      } catch (err) {
        console.error("Error approving installation:", err);
        alert(`Error: ${err.message}`);
      } finally {
        setApprovingId(null);
      }
    }
  };

  // Enhanced Excel file validation with multiple format support
  const validateAndParseExcel = async (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          
          // Try to read with different options
          let workbook;
          let parseErrors = [];
          
          // Attempt 1: Standard read
          try {
            workbook = XLSX.read(data, { type: 'array', cellDates: false, defval: "" });
          } catch (err1) {
            parseErrors.push(`Standard read failed: ${err1.message}`);
            
            // Attempt 2: Try with different encoding
            try {
              workbook = XLSX.read(data, { type: 'array', cellHTML: false, cellFormula: false });
            } catch (err2) {
              parseErrors.push(`Alternative read failed: ${err2.message}`);
              throw new Error(`Failed to parse Excel file: ${parseErrors.join('; ')}`);
            }
          }
          
          if (!workbook.SheetNames || workbook.SheetNames.length === 0) {
            throw new Error("Excel file has no sheets");
          }
          
          const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
          
          // Convert to JSON with various options
          let jsonData;
          try {
            // Try with header option
            jsonData = XLSX.utils.sheet_to_json(firstSheet, { defval: "" });
          } catch (err) {
            try {
              // Try without header option
              jsonData = XLSX.utils.sheet_to_json(firstSheet, { header: 1 });
              if (jsonData.length > 0 && jsonData[0].length > 0) {
                // Use first row as headers
                const headers = jsonData[0];
                jsonData = jsonData.slice(1).map(row => {
                  const obj = {};
                  headers.forEach((header, idx) => {
                    obj[header] = row[idx] || "";
                  });
                  return obj;
                });
              }
            } catch (err2) {
              throw new Error(`Failed to convert sheet to JSON: ${err2.message}`);
            }
          }
          
          if (!jsonData || jsonData.length === 0) {
            throw new Error("Excel file is empty");
          }
          
          // Find saralId column (case insensitive)
          const sampleRow = jsonData[0];
          const saralIdKey = Object.keys(sampleRow).find(key => 
            key.toLowerCase() === 'saralid' || 
            key.toLowerCase() === 'saral_id' || 
            key.toLowerCase() === 'saral id'
          );
          
          if (!saralIdKey) {
            throw new Error(`Excel file must contain a 'saralId' column. Found columns: ${Object.keys(sampleRow).join(', ')}`);
          }
          
          // Extract and validate saralIds
          const saralIds = [];
          const invalidRows = [];
          
          jsonData.forEach((row, index) => {
            const saralId = row[saralIdKey];
            if (saralId && typeof saralId === 'string' && saralId.trim()) {
              saralIds.push(saralId.trim());
            } else if (saralId && typeof saralId !== 'string') {
              saralIds.push(String(saralId).trim());
            } else if (!saralId || (typeof saralId === 'string' && !saralId.trim())) {
              invalidRows.push(index + 2); // +2 because of 0-index and header row
            }
          });
          
          if (saralIds.length === 0) {
            throw new Error("No valid Saral IDs found in the Excel file");
          }
          
          resolve({
            jsonData,
            saralIds,
            sheetName: workbook.SheetNames[0],
            rowCount: jsonData.length,
            validCount: saralIds.length,
            invalidRows,
            saralIdKey
          });
          
        } catch (error) {
          reject(error);
        }
      };
      
      reader.onerror = () => reject(new Error("Failed to read file"));
      reader.readAsArrayBuffer(file);
    });
  };

  // Handle Excel file selection with enhanced validation
  const handleExcelFileSelect = async (event) => {
    const file = event.target.files[0];
    setBulkApproveResult(null);
    setExcelDataPreview([]);
    setExcelValidationErrors([]);
    
    if (!file) return;
    
    // Validate file extension
    const fileExtension = file.name.split('.').pop().toLowerCase();
    const validExtensions = ['xlsx', 'xls', 'xlsm', 'xlsb'];
    
    if (!validExtensions.includes(fileExtension)) {
      setBulkApproveResult({
        success: false,
        message: "Invalid file type. Please upload an Excel file (.xlsx, .xls, .xlsm, or .xlsb).",
        errors: [`File extension: .${fileExtension} is not supported`],
      });
      setSelectedExcelFile(null);
      event.target.value = "";
      return;
    }
    
    // Validate file size (max 10 MB for better compatibility)
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      setBulkApproveResult({
        success: false,
        message: "File is too large. Maximum size is 10 MB.",
        errors: [`File size: ${(file.size / 1024 / 1024).toFixed(2)} MB`],
      });
      setSelectedExcelFile(null);
      event.target.value = "";
      return;
    }
    
    // Validate and parse Excel
    try {
      const validation = await validateAndParseExcel(file);
      
      setExcelDataPreview(validation.saralIds.slice(0, 10));
      setExcelValidationErrors(validation.invalidRows.map(row => `Row ${row}: Empty Saral ID`));
      
      setSelectedExcelFile(file);
      setBulkApproveResult({
        success: true,
        message: `✅ File validated successfully! Found ${validation.validCount} valid Saral IDs out of ${validation.rowCount} total rows.`,
        errors: validation.invalidRows.length > 0 ? [`${validation.invalidRows.length} rows have empty Saral IDs`] : [],
      });
      
    } catch (validationError) {
      console.error("Excel validation error:", validationError);
      setBulkApproveResult({
        success: false,
        message: "Excel file validation failed",
        errors: [validationError.message],
      });
      setSelectedExcelFile(null);
      event.target.value = "";
    }
  };

  // Bulk approval with FormData and proper error handling
  const uploadAndBulkApprove = async () => {
    if (!selectedExcelFile) {
      setBulkApproveResult({
        success: false,
        message: "Please select an Excel file first.",
        errors: [],
      });
      return;
    }

    setUploadingExcel(true);
    setBulkApproveResult(null);

    // Create FormData
    const formData = new FormData();
    formData.append("file", selectedExcelFile);
    formData.append("updatedByName", empData?.name || "");
    formData.append("updatedByEmpId", empData?._id || "");
    formData.append("department", empData?.department || "");

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL_API_URL}/service-team/approve-installations/excel`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
          timeout: 120000,
        }
      );

      if (response.data.success) {
        setBulkApproveResult({
          success: true,
          message: response.data.message || "Bulk approval completed successfully!",
          data: response.data.data,
          errors: [],
        });

        // Refresh data after successful bulk approval
        const department = empData?.department || "";
        const allData = await fetchAllInstallationData(selectedState, department);
        setInstallationData(allData);
        setFilteredInstallations(allData);

        // Close modal after 3 seconds on success
        setTimeout(() => {
          setExcelUploadModal(false);
          setSelectedExcelFile(null);
          setExcelDataPreview([]);
          setExcelValidationErrors([]);
        }, 3000);
      } else {
        // Backend returned success:false
        const errorMessage = response.data.message || "Bulk approval failed.";
        const errorDetails = response.data.errors || [];
        
        setBulkApproveResult({
          success: false,
          message: errorMessage,
          errors: errorDetails.length ? errorDetails : [errorMessage],
          data: response.data.data,
        });
      }
    } catch (err) {
      console.error("Bulk Approval Error:", err);
      
      let errorMessage = "Failed to process bulk approval. Please try again.";
      let errorDetails = [];

      if (err.code === "ECONNABORTED") {
        errorMessage = "Request timed out. The server took too long to respond.";
        errorDetails = ["Timeout error: Server didn't respond within 2 minutes"];
      } else if (err.response) {
        errorMessage = err.response.data?.message || `Server error: ${err.response.status}`;
        errorDetails = err.response.data?.errors || [];
        
        if (err.response.status === 400) {
          errorMessage = "Bad Request: Invalid Excel format or data";
          errorDetails = ["Please check your Excel file format and column names"];
        } else if (err.response.status === 401) {
          errorMessage = "Unauthorized: Please login again";
          errorDetails = ["Authentication failed"];
        } else if (err.response.status === 403) {
          errorMessage = "Forbidden: You don't have permission to perform bulk approval";
          errorDetails = ["Insufficient permissions"];
        } else if (err.response.status === 500) {
          errorMessage = "Server error. Please contact support.";
          errorDetails = ["Internal server error occurred"];
        }
        
        if (!errorDetails.length && err.response.data?.error) {
          errorDetails = [err.response.data.error];
        }
      } else if (err.request) {
        errorMessage = "No response from server. Please check your network connection.";
        errorDetails = ["Network error: Server unreachable"];
      } else {
        errorMessage = err.message || "An unexpected error occurred.";
        errorDetails = [err.message];
      }

      setBulkApproveResult({
        success: false,
        message: errorMessage,
        errors: errorDetails,
      });
    } finally {
      setUploadingExcel(false);
    }
  };

  const closeExcelModal = () => {
    setExcelUploadModal(false);
    setSelectedExcelFile(null);
    setBulkApproveResult(null);
    setExcelDataPreview([]);
    setExcelValidationErrors([]);
  };

  // Download sample Excel template with proper format
  const downloadSampleExcel = () => {
    const sampleData = [
      { saralId: "SARAL001" },
      { saralId: "SARAL002" },
      { saralId: "SARAL003" },
      { saralId: "SARAL004" },
      { saralId: "SARAL005" },
    ];
    
    const worksheet = XLSX.utils.json_to_sheet(sampleData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "SaralIDs");
    
    // Set column width
    worksheet['!cols'] = [{ wch: 20 }];
    
    XLSX.writeFile(workbook, "bulk_approval_sample.xlsx");
  };

  // Decline with remark
  const handleDeclineClick = (installationId) => {
    setDeclineInstallationId(installationId);
    setRemarkModalOpen(true);
    setSelectedRemarkId(null);
  };

  const closeRemarkModal = () => {
    setRemarkModalOpen(false);
    setDeclineInstallationId(null);
    setSelectedRemarkId(null);
  };

  const submitDecline = async () => {
    if (!selectedRemarkId) {
      alert("Please select a remark");
      return;
    }
    setDecliningId(declineInstallationId);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL_API_URL}/service-team/reject-installation-data`,
        {
          department: empData?.department,
          installationId: declineInstallationId,
          remarkId: selectedRemarkId,
          updatedByName: empData?.name,
          updatedByEmpId: empData?._id,
        },
      );
      if (response.data.success) {
        setInstallationData((prev) =>
          prev.map((item) =>
            item._id === declineInstallationId
              ? { ...item, stageId: { ...item.stageId, stage: "Rejected By VT-2" }, status: "declined", remarks: response.data.remarkText || null }
              : item,
          ),
        );
        alert("Installation declined successfully.");
        closeRemarkModal();
      } else {
        throw new Error(response.data.message || "Failed to decline");
      }
    } catch (err) {
      console.error("Error declining installation:", err);
      alert(`Error: ${err.message}`);
    } finally {
      setDecliningId(null);
      setDeclineInstallationId(null);
    }
  };

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      if (installationsGridRef.current) {
        installationsGridRef.current.scrollTop = 0;
      }
    }
  };

  const getStageCount = (stage) => {
    if (stage === "all") return installationData.length;
    if (stage === "pending") {
      return installationData.filter(item => isPendingStatus(getDisplayStatus(item))).length;
    }
    return installationData.filter(item => getDisplayStatus(item) === stage).length;
  };

  if (loading) {
    return (
      <div className="flex flex-col justify-center items-center h-screen text-xl">
        {fetchProgress.total > 0 ? (
          <>
            <FaSpinner className="animate-spin text-4xl mb-4 text-blue-600" />
            <p className="mb-2">Loading installation data...</p>
            <p className="text-sm text-gray-600">
              Fetching page {fetchProgress.current} of {fetchProgress.total}
            </p>
            <div className="w-64 h-2 bg-gray-200 rounded-full mt-2 overflow-hidden">
              <div 
                className="h-full bg-blue-600 transition-all duration-300"
                style={{ width: `${(fetchProgress.current / fetchProgress.total) * 100}%` }}
              />
            </div>
          </>
        ) : (
          <>
            <FaSpinner className="animate-spin text-4xl mb-4 text-blue-600" />
            <p>Loading installation data...</p>
          </>
        )}
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col justify-center items-center h-screen text-xl text-red-600">
        <p className="mb-4">Error: {error}</p>
        <button 
          onClick={() => window.location.reload()}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 p-3 sm:p-4 md:p-6 relative">
      {/* Image/Video Modal */}
      {selectedImage.url && (
        <div
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-2 sm:p-4"
          onClick={closeImage}
        >
          <div
            className="relative max-w-5xl w-full bg-white rounded-lg overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={closeImage}
              className="absolute top-2 right-2 text-2xl sm:text-3xl text-gray-600 hover:text-gray-900 z-10"
            >
              &times;
            </button>
            {/\.(mp4|webm|ogg|mov)$/i.test(selectedImage.url) ? (
              <video
                src={selectedImage.url}
                controls
                className="w-full h-auto max-h-[80vh] object-contain"
              />
            ) : (
              <img
                src={selectedImage.url}
                alt="Full screen"
                className="w-full h-auto max-h-[80vh] object-contain"
              />
            )}
            <div className="flex flex-col sm:flex-row justify-end gap-2 p-3 bg-gray-100">
              <button
                onClick={() =>
                  downloadImage(
                    selectedImage.url,
                    selectedImage.fieldName,
                    selectedImage.saralId,
                    selectedImage.date,
                  )
                }
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center justify-center gap-1 text-sm sm:text-base"
              >
                <FaDownload /> Download
              </button>
              <button
                onClick={closeImage}
                className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 flex items-center justify-center gap-1 text-sm sm:text-base"
              >
                <FaTimes /> Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Excel Upload Modal */}
      {excelUploadModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-2 sm:p-4"
          onClick={closeExcelModal}
        >
          <div
            className="relative max-w-2xl w-full bg-white rounded-lg overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-3 sm:p-4 border-b flex justify-between items-center">
              <h2 className="text-lg sm:text-xl font-semibold">
                Bulk Approve Installations via Excel
              </h2>
              <button
                onClick={closeExcelModal}
                className="text-2xl text-gray-600 hover:text-gray-900"
              >
                &times;
              </button>
            </div>
            <div className="p-3 sm:p-4 max-h-[60vh] overflow-y-auto">
              {/* Instructions */}
              <div className="mb-4 p-3 bg-blue-50 rounded border border-blue-200">
                <h3 className="font-semibold text-sm text-blue-800 mb-2 flex items-center gap-2">
                  <FaInfoCircle /> Instructions:
                </h3>
                <ul className="text-xs text-blue-700 list-disc list-inside space-y-1">
                  <li>Excel file must have a column named <strong>"saralId"</strong> (case-insensitive)</li>
                  <li>Supported formats: <strong>.xlsx, .xls, .xlsm, .xlsb</strong></li>
                  <li>Maximum file size: <strong>10 MB</strong></li>
                  <li>Each Saral ID will be validated and approved if found in the system</li>
                  <li>Empty Saral IDs will be automatically skipped</li>
                </ul>
                <button
                  onClick={downloadSampleExcel}
                  className="mt-3 text-xs bg-green-600 text-white px-3 py-1.5 rounded hover:bg-green-700 flex items-center gap-2"
                >
                  <FaFileExcel /> Download Sample Excel Template
                </button>
              </div>

              {/* File selection */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Upload Excel File with Saral IDs
                </label>
                <input
                  type="file"
                  accept=".xlsx,.xls,.xlsm,.xlsb"
                  onChange={handleExcelFileSelect}
                  className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                {selectedExcelFile && (
                  <p className="text-sm text-green-600 mt-1">
                    ✓ Selected: {selectedExcelFile.name} ({(selectedExcelFile.size / 1024).toFixed(1)} KB)
                  </p>
                )}
              </div>

              {/* Data Preview */}
              {excelDataPreview.length > 0 && (
                <div className="mb-4">
                  <h3 className="text-sm font-semibold text-gray-700 mb-2">
                    Data Preview (First 10 Saral IDs):
                  </h3>
                  <div className="bg-gray-50 p-2 rounded border max-h-32 overflow-y-auto">
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-1 text-xs">
                      {excelDataPreview.map((id, idx) => (
                        <div key={idx} className="font-mono text-gray-600 p-1 bg-white rounded">
                          {id}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Validation Warnings */}
              {excelValidationErrors.length > 0 && (
                <div className="mb-4 p-2 bg-yellow-50 rounded border border-yellow-200">
                  <p className="text-xs font-semibold text-yellow-700 mb-1">⚠️ Warnings:</p>
                  <ul className="text-xs text-yellow-600 list-disc list-inside">
                    {excelValidationErrors.slice(0, 3).map((error, idx) => (
                      <li key={idx}>{error}</li>
                    ))}
                    {excelValidationErrors.length > 3 && (
                      <li>...and {excelValidationErrors.length - 3} more</li>
                    )}
                  </ul>
                </div>
              )}

              {/* Result Display */}
              {bulkApproveResult && (
                <div
                  className={`mb-4 p-3 rounded border ${
                    bulkApproveResult.success
                      ? "bg-green-50 border-green-200"
                      : "bg-red-50 border-red-200"
                  }`}
                >
                  <p
                    className={`text-sm font-semibold ${
                      bulkApproveResult.success ? "text-green-700" : "text-red-700"
                    }`}
                  >
                    {bulkApproveResult.success ? "✓ Success!" : "✗ Error:"}
                  </p>
                  <p
                    className={`text-sm mt-1 ${
                      bulkApproveResult.success ? "text-green-600" : "text-red-600"
                    }`}
                  >
                    {bulkApproveResult.message}
                  </p>
                  
                  {/* Detailed error list */}
                  {!bulkApproveResult.success && bulkApproveResult.errors && bulkApproveResult.errors.length > 0 && (
                    <div className="mt-2">
                      <p className="text-xs text-red-600 font-semibold">Details:</p>
                      <ul className="list-disc list-inside text-xs text-red-600 max-h-32 overflow-y-auto">
                        {bulkApproveResult.errors.map((err, idx) => (
                          <li key={idx}>{err}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  {/* Success details */}
                  {bulkApproveResult.success && bulkApproveResult.data && (
                    <div className="mt-2 text-xs text-green-600 space-y-1">
                      <p>✓ Approved Count: {bulkApproveResult.data.approvedCount || 0}</p>
                      {bulkApproveResult.data.failedCount > 0 && (
                        <>
                          <p className="text-red-600">✗ Failed Count: {bulkApproveResult.data.failedCount || 0}</p>
                        </>
                      )}
                      {bulkApproveResult.data.failedSaralIds && bulkApproveResult.data.failedSaralIds.length > 0 && (
                        <>
                          <p className="font-semibold mt-2 text-red-700">Failed Saral IDs:</p>
                          <div className="max-h-24 overflow-y-auto">
                            {bulkApproveResult.data.failedSaralIds.map((id, idx) => (
                              <div key={idx} className="text-red-600 font-mono">{id}</div>
                            ))}
                          </div>
                        </>
                      )}
                      {bulkApproveResult.data.approvedSaralIds && bulkApproveResult.data.approvedSaralIds.length > 0 && (
                        <>
                          <p className="font-semibold mt-2 text-green-700">Approved Saral IDs:</p>
                          <div className="max-h-24 overflow-y-auto">
                            {bulkApproveResult.data.approvedSaralIds.slice(0, 5).map((id, idx) => (
                              <div key={idx} className="text-green-600 font-mono">{id}</div>
                            ))}
                            {bulkApproveResult.data.approvedSaralIds.length > 5 && (
                              <div className="text-gray-500">...and {bulkApproveResult.data.approvedSaralIds.length - 5} more</div>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="p-3 sm:p-4 border-t flex flex-col sm:flex-row justify-end gap-2">
              <button
                onClick={closeExcelModal}
                className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 flex items-center justify-center gap-1 text-sm sm:text-base"
              >
                <FaTimes /> Close
              </button>
              <button
                onClick={uploadAndBulkApprove}
                disabled={!selectedExcelFile || uploadingExcel}
                className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 flex items-center justify-center gap-1 text-sm sm:text-base"
              >
                {uploadingExcel ? (
                  <>
                    <FaSpinner className="animate-spin" /> Processing...
                  </>
                ) : (
                  <>
                    <FaUpload /> Upload & Approve
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Remark Modal */}
      {remarkModalOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-2 sm:p-4"
          onClick={closeRemarkModal}
        >
          <div
            className="relative max-w-md w-full bg-white rounded-lg overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-4 border-b flex justify-between items-center">
              <h2 className="text-lg sm:text-xl font-semibold">
                Select Rejection Remark
              </h2>
              <button
                onClick={closeRemarkModal}
                className="text-2xl text-gray-600 hover:text-gray-900"
              >
                &times;
              </button>
            </div>
            <div className="p-4 max-h-[60vh] overflow-y-auto">
              {remarksList.length === 0 ? (
                <p className="text-gray-500">Loading remarks...</p>
              ) : (
                <div className="space-y-2">
                  {remarksList.map((remark) => (
                    <label
                      key={remark._id}
                      className="flex items-center gap-2 p-2 border rounded hover:bg-gray-50 cursor-pointer"
                    >
                      <input
                        type="radio"
                        name="remark"
                        value={remark._id}
                        checked={selectedRemarkId === remark._id}
                        onChange={() => setSelectedRemarkId(remark._id)}
                        className="w-4 h-4"
                      />
                      <span className="text-sm sm:text-base">
                        {remark.remark}
                      </span>
                    </label>
                  ))}
                </div>
              )}
            </div>
            <div className="p-4 border-t flex justify-end gap-2">
              <button
                onClick={closeRemarkModal}
                className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 flex items-center gap-1 text-sm sm:text-base"
              >
                <FaTimes /> Cancel
              </button>
              <button
                onClick={submitDecline}
                disabled={!selectedRemarkId || decliningId === declineInstallationId}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50 flex items-center gap-1 text-sm sm:text-base"
              >
                {decliningId === declineInstallationId ? (
                  <>
                    <FaSpinner className="animate-spin" /> Processing...
                  </>
                ) : (
                  <>
                    <FaBan /> Confirm Decline
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="mb-4 sm:mb-6">
        <h1 className="text-xl sm:text-1xl font-bold text-center mb-3 sm:mb-4">
          VERIFICATION IMAGE - {selectedState.toUpperCase()}
        </h1>

        {/* Filters */}
        <div className="flex flex-col gap-3">
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 items-stretch sm:items-center justify-between">
            <div className="flex flex-col xs:flex-row items-stretch xs:items-center gap-2 w-full sm:w-auto">
              <label
                htmlFor="state-select"
                className="font-medium text-sm sm:text-base whitespace-nowrap"
              >
                Filter by State:
              </label>
              <select
                id="state-select"
                value={selectedState}
                onChange={handleStateChange}
                className="border rounded px-3 py-2 bg-white w-full xs:w-auto sm:w-48 text-sm sm:text-base"
              >
                {states.map((state, index) => (
                  <option key={index} value={state}>
                    {state}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex flex-col xs:flex-row items-stretch xs:items-center gap-2">
              <label
                htmlFor="stage-select"
                className="font-medium text-sm sm:text-base whitespace-nowrap"
              >
                Filter by Stage:
              </label>
              <div className="flex flex-wrap gap-2">
                {stageOptions.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => handleStageChange({ target: { value: option.value } })}
                    className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                      selectedStage === option.value
                        ? "bg-blue-600 text-white"
                        : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                    }`}
                  >
                    {option.label}
                    <span className="ml-2 px-2 py-0.5 text-xs rounded-full bg-white bg-opacity-30 text-inherit">
                      {getStageCount(option.value)}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            <div className="flex flex-col xs:flex-row items-stretch xs:items-center gap-2 w-full sm:w-auto">
              <input
                type="text"
                placeholder="Search by Saral ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="border rounded px-3 py-2 w-full xs:w-64 text-sm sm:text-base"
              />
              <span className="text-sm text-gray-600 self-center whitespace-nowrap">
                Total Records: {filteredInstallations.length}
              </span>
            </div>
          </div>

          <div className="flex justify-end">
            <button
              onClick={() => setExcelUploadModal(true)}
              disabled={uploadingExcel}
              className="px-4 py-2 bg-blue-700 text-white rounded text-sm sm:text-base flex items-center justify-center gap-2 hover:bg-blue-800 transition disabled:opacity-50"
            >
              <FaUpload className="text-lg" /> Bulk Approve via Excel
            </button>
          </div>
        </div>
      </div>

      {/* Pagination Controls - Top */}
      {totalPages > 1 && (
        <div className="flex justify-end gap-2 mb-4">
          <button
            onClick={() => goToPage(currentPage - 1)}
            disabled={currentPage === 1}
            className="px-3 py-1 bg-yellow-400 text-dark rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-yellow-500 transition"
          >
            Previous
          </button>
          <span className="text-sm flex items-center">
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={() => goToPage(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="px-3 py-1 bg-yellow-400 text-dark rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-yellow-500 transition"
          >
            Next
          </button>
        </div>
      )}

      {/* Installations List */}
      <div
        ref={installationsGridRef}
        className="space-y-4 max-h-[calc(100vh-300px)] overflow-y-auto pr-1 sm:pr-2"
      >
        {paginatedData.length > 0 ? (
          paginatedData.map((installation) => {
            const displayStatus = getDisplayStatus(installation);
            const isDecliningThis = decliningId === installation._id;
            const finalStatus = isFinalStatus(displayStatus);

            return (
              <div
                key={installation._id}
                className="bg-white rounded-lg shadow p-3 sm:p-4"
              >
                <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-3 border-b pb-3 mb-3">
                  <div className="flex flex-wrap items-center gap-2 text-sm sm:text-base">
                    <h3 className="font-semibold text-base sm:text-lg">
                      Saral ID: {installation.farmerSaralId || "N/A"}
                    </h3>
                    <span className="text-xs sm:text-sm text-gray-500">
                      {formatDate(installation.createdAt)}
                    </span>
                    {installation.state && (
                      <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded">
                        {installation.state}
                      </span>
                    )}
                    {displayStatus && (
                      <span
                        className={`text-xs px-2 py-1 rounded flex items-center gap-1 ${
                          displayStatus === "Approved By VT-2"
                            ? "bg-green-100 text-green-800"
                            : displayStatus === "Rejected By VT-2"
                              ? "bg-red-100 text-red-800"
                              : "bg-yellow-100 text-yellow-800"
                        }`}
                      >
                        {displayStatus === "Approved By VT-2" && <FaCheck size={12} />}
                        {displayStatus === "Rejected By VT-2" && <FaBan size={12} />}
                        {displayStatus === "Pending" && <FaSpinner size={12} className="animate-spin" />}
                        {displayStatus || "Pending"}
                      </span>
                    )}
                    {displayStatus === "Rejected By VT-2" && installation.remarks && (
                      <span className="text-xs text-red-700 bg-red-50 px-2 py-1 rounded">
                        Remark: {installation.remarks}
                      </span>
                    )}
                  </div>

                  <div className="flex flex-col xs:flex-row xs:flex-wrap items-start xs:items-center gap-3 w-full lg:w-auto">
                    <div className="flex gap-2 text-xs sm:text-sm">
                      <span>
                        <span className="font-medium">Lat:</span>{" "}
                        {installation.latitude || "N/A"}
                      </span>
                      <span>
                        <span className="font-medium">Lng:</span>{" "}
                        {installation.longitude || "N/A"}
                      </span>
                    </div>
                    {!finalStatus && (
                      <div className="flex flex-wrap gap-2 w-full xs:w-auto">
                        <button
                          onClick={() => handleApprove(installation._id)}
                          disabled={approvingId === installation._id || isDecliningThis}
                          className={`px-3 py-1 bg-green-600 text-white rounded text-xs sm:text-sm flex items-center gap-1 ${
                            approvingId === installation._id || isDecliningThis
                              ? "opacity-50 cursor-not-allowed"
                              : "hover:bg-green-700"
                          } flex-1 xs:flex-none`}
                        >
                          {approvingId === installation._id ? (
                            <>
                              <FaSpinner className="animate-spin" /> Approving...
                            </>
                          ) : (
                            <>
                              <FaCheck /> Approve
                            </>
                          )}
                        </button>
                        <button
                          onClick={() => handleDeclineClick(installation._id)}
                          disabled={approvingId === installation._id || isDecliningThis}
                          className={`px-3 py-1 bg-red-600 text-white rounded text-xs sm:text-sm flex items-center gap-1 ${
                            approvingId === installation._id || isDecliningThis
                              ? "opacity-50 cursor-not-allowed"
                              : "hover:bg-red-700"
                          } flex-1 xs:flex-none`}
                        >
                          {isDecliningThis ? (
                            <>
                              <FaSpinner className="animate-spin" /> Declining...
                            </>
                          ) : (
                            <>
                              <FaTimes /> Decline
                            </>
                          )}
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Photo Sections */}
                <div className="grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                  {installation.pitPhoto?.length > 0 && (
                    <div className="border rounded p-2">
                      <h4 className="font-medium text-xs sm:text-sm mb-2">Pit Photo</h4>
                      <div className="flex flex-row flex-nowrap gap-2 overflow-x-auto pb-1">
                        {installation.pitPhoto.slice(0, 4).map((photo, index) => (
                          <img
                            key={`pit-${index}`}
                            src={getFullImageUrl(photo)}
                            alt={`Pit ${index + 1}`}
                            className="w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 object-cover rounded cursor-pointer hover:scale-105 transition flex-shrink-0"
                            onClick={() =>
                              openImage(photo, "pit_photo", installation.farmerSaralId, installation.createdAt)
                            }
                          />
                        ))}
                      </div>
                    </div>
                  )}
                  {installation.earthingFarmerPhoto?.length > 0 && (
                    <div className="border rounded p-2">
                      <h4 className="font-medium text-xs sm:text-sm mb-2">Earthing</h4>
                      <div className="flex flex-row flex-nowrap gap-2 overflow-x-auto pb-1">
                        {installation.earthingFarmerPhoto.slice(0, 4).map((photo, index) => (
                          <img
                            key={`earthing-${index}`}
                            src={getFullImageUrl(photo)}
                            alt={`Earthing ${index + 1}`}
                            className="w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 object-cover rounded cursor-pointer hover:scale-105 transition flex-shrink-0"
                            onClick={() =>
                              openImage(photo, "earthing_photo", installation.farmerSaralId, installation.createdAt)
                            }
                          />
                        ))}
                      </div>
                    </div>
                  )}
                  {installation.antiTheftNutBoltPhoto?.length > 0 && (
                    <div className="border rounded p-2">
                      <h4 className="font-medium text-xs sm:text-sm mb-2">Anti Theft</h4>
                      <div className="flex flex-row flex-nowrap gap-2 overflow-x-auto pb-1">
                        {installation.antiTheftNutBoltPhoto.slice(0, 4).map((photo, index) => (
                          <img
                            key={`antiTheft-${index}`}
                            src={getFullImageUrl(photo)}
                            alt={`Anti Theft ${index + 1}`}
                            className="w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 object-cover rounded cursor-pointer hover:scale-105 transition flex-shrink-0"
                            onClick={() =>
                              openImage(photo, "anti_theft_nut_bolt", installation.farmerSaralId, installation.createdAt)
                            }
                          />
                        ))}
                      </div>
                    </div>
                  )}
                  {installation.lightingArresterInstallationPhoto?.length > 0 && (
                    <div className="border rounded p-2">
                      <h4 className="font-medium text-xs sm:text-sm mb-2">Lightning</h4>
                      <div className="flex flex-row flex-nowrap gap-2 overflow-x-auto pb-1">
                        {installation.lightingArresterInstallationPhoto.slice(0, 4).map((photo, index) => (
                          <img
                            key={`lightning-${index}`}
                            src={getFullImageUrl(photo)}
                            alt={`Lightning ${index + 1}`}
                            className="w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 object-cover rounded cursor-pointer hover:scale-105 transition flex-shrink-0"
                            onClick={() =>
                              openImage(photo, "lightning_arrester", installation.farmerSaralId, installation.createdAt)
                            }
                          />
                        ))}
                      </div>
                    </div>
                  )}
                  {installation.finalFoundationFarmerPhoto?.length > 0 && (
                    <div className="border rounded p-2">
                      <h4 className="font-medium text-xs sm:text-sm mb-2">Foundation</h4>
                      <div className="flex flex-row flex-nowrap gap-2 overflow-x-auto pb-1">
                        {installation.finalFoundationFarmerPhoto.slice(0, 4).map((photo, index) => (
                          <img
                            key={`foundation-${index}`}
                            src={getFullImageUrl(photo)}
                            alt={`Foundation ${index + 1}`}
                            className="w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 object-cover rounded cursor-pointer hover:scale-105 transition flex-shrink-0"
                            onClick={() =>
                              openImage(photo, "final_foundation", installation.farmerSaralId, installation.createdAt)
                            }
                          />
                        ))}
                      </div>
                    </div>
                  )}
                  {installation.panelFarmerPhoto?.length > 0 && (
                    <div className="border rounded p-2">
                      <h4 className="font-medium text-xs sm:text-sm mb-2">Panel</h4>
                      <div className="flex flex-row flex-nowrap gap-2 overflow-x-auto pb-1">
                        {installation.panelFarmerPhoto.slice(0, 4).map((photo, index) => (
                          <img
                            key={`panel-${index}`}
                            src={getFullImageUrl(photo)}
                            alt={`Panel ${index + 1}`}
                            className="w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 object-cover rounded cursor-pointer hover:scale-105 transition flex-shrink-0"
                            onClick={() =>
                              openImage(photo, "panel", installation.farmerSaralId, installation.createdAt)
                            }
                          />
                        ))}
                      </div>
                    </div>
                  )}
                  {installation.controllerBoxFarmerPhoto?.length > 0 && (
                    <div className="border rounded p-2">
                      <h4 className="font-medium text-xs sm:text-sm mb-2">Controller</h4>
                      <div className="flex flex-row flex-nowrap gap-2 overflow-x-auto pb-1">
                        {installation.controllerBoxFarmerPhoto.slice(0, 4).map((photo, index) => (
                          <img
                            key={`controller-${index}`}
                            src={getFullImageUrl(photo)}
                            alt={`Controller ${index + 1}`}
                            className="w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 object-cover rounded cursor-pointer hover:scale-105 transition flex-shrink-0"
                            onClick={() =>
                              openImage(photo, "controller_box", installation.farmerSaralId, installation.createdAt)
                            }
                          />
                        ))}
                      </div>
                    </div>
                  )}
                  {installation.waterDischargeFarmerPhoto?.length > 0 && (
                    <div className="border rounded p-2">
                      <h4 className="font-medium text-xs sm:text-sm mb-2">Water Discharge</h4>
                      <div className="flex flex-row flex-nowrap gap-2 overflow-x-auto pb-1">
                        {installation.waterDischargeFarmerPhoto.slice(0, 4).map((photo, index) => (
                          <img
                            key={`discharge-${index}`}
                            src={getFullImageUrl(photo)}
                            alt={`Discharge ${index + 1}`}
                            className="w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 object-cover rounded cursor-pointer hover:scale-105 transition flex-shrink-0"
                            onClick={() =>
                              openImage(photo, "water_discharge", installation.farmerSaralId, installation.createdAt)
                            }
                          />
                        ))}
                      </div>
                    </div>
                  )}
                  {installation.installationVideo?.length > 0 && (
                    <div className="border rounded p-2">
                      <h4 className="font-medium text-xs sm:text-sm mb-2">Installation Video</h4>
                      <div className="flex flex-row flex-nowrap gap-2 overflow-x-auto pb-1">
                        {installation.installationVideo.map((video, index) => (
                          <video
                            key={`video-${index}`}
                            src={getFullImageUrl(video)}
                            controls
                            className="w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 object-cover rounded cursor-pointer hover:scale-105 transition flex-shrink-0"
                            onClick={(e) => {
                              e.stopPropagation();
                              openImage(video, "installation_video", installation.farmerSaralId, installation.createdAt);
                            }}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })
        ) : (
          <div className="text-center py-10 text-gray-500">
            No installations found {searchTerm ? "matching your search" : ""}
            {selectedState ? ` in ${selectedState}` : ""}
            {selectedStage !== "all" ? ` with stage "${stageOptions.find(opt => opt.value === selectedStage)?.label}"` : ""}
          </div>
        )}
      </div>

      {/* Scroll to Top Button */}
      {showScrollTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-6 right-6 bg-blue-600 text-white w-10 h-10 sm:w-12 sm:h-12 rounded-full shadow-lg flex items-center justify-center text-xl sm:text-2xl hover:bg-blue-700 transition z-40"
        >
          <FaArrowUp />
        </button>
      )}
    </div>
  );
};

export default VerifyImage;